/**
 * First-launch Permissions onboarding screen.
 *
 * Shown ONCE per app install per role-flavour. After the user taps
 * "Allow" we trigger the actual permission prompts via standard
 * browser APIs (``navigator.geolocation``, ``getUserMedia``,
 * ``Notification.requestPermission``). Inside a Capacitor WebView on
 * Android/iOS, the OS-level prompt fires automatically — no extra
 * Capacitor plugins required.
 *
 * Per-app onboarding set (iter53 — Camera is NEVER asked here, it's
 * always requested in-context inside ImageUploader / camera-picker
 * components, per Play Store / App Store policy):
 *   * Customer APK (Customer + Merchant union) → location, notifications
 *   * Rider APK                                → location, notifications
 *
 * Skipped automatically:
 *   * When ``localStorage.bizil_permissions_done`` is already truthy.
 *   * When running in SSR / non-browser context.
 */
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MapPin, Bell, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { requestLocationPermission } from "../lib/geolocation";
import { requestPushPermission } from "../lib/capacitor";

const DONE_KEY = "bizil_permissions_done";

// iter53 — Camera is intentionally absent from every descriptor. It is
// requested in-context at the moment the user taps an upload control
// (see lib/permissions.js + components/ImageUploader.jsx). Store-policy
// rationale: Apple/Google reject onboarding flows that request camera
// "just in case" without an immediate use.
const APP_DESCRIPTORS = {
    customer: [
        { id: "location", icon: MapPin, color: "text-blue-600",
            title: "Location",
            body: "So we can show shops near you and auto-fill your delivery address." },
        { id: "notifications", icon: Bell, color: "text-amber-600",
            title: "Notifications",
            body: "Order updates, delivery status, exclusive offers and coin reminders." },
    ],
    // Kept for backward-compat with old deep links — same as customer
    // because the Customer APK now bundles the merchant console too.
    merchant: [
        { id: "notifications", icon: Bell, color: "text-amber-600",
            title: "Notifications",
            body: "New orders, customer cancellations, payout alerts — never miss a sale." },
    ],
    rider: [
        { id: "location", icon: MapPin, color: "text-blue-600",
            title: "Location",
            body: "So shops and customers can see your live position during a delivery." },
        { id: "notifications", icon: Bell, color: "text-amber-600",
            title: "Notifications",
            body: "Loud alerts when a new order is available in your zone." },
    ],
};

export default function PermissionsPage() {
    const nav = useNavigate();
    const loc = useLocation();
    // ``?app=customer|merchant|rider`` — defaults to ``customer``.
    const app = new URLSearchParams(loc.search).get("app") || "customer";
    const next = new URLSearchParams(loc.search).get("next") || "/";
    const items = APP_DESCRIPTORS[app] || APP_DESCRIPTORS.customer;
    const [busy, setBusy] = useState(false);

    const requestLocation = async () => {
        // Uses the cross-platform helper so on Android / iOS we
        // trigger the *native* OS prompt (via @capacitor/geolocation)
        // and on web we fall back to navigator.geolocation.
        // ``requestLocationPermission()`` now returns a structured
        // ``{state, coords?, error?}`` object (it used to be a bare
        // string) — unwrap ``.state`` here so ``allow()``'s
        // ``v === "granted"`` count below keeps working, matching
        // ``requestNotifications()`` just below, which already returns
        // a plain string.
        const r = await requestLocationPermission();
        return r.state;   // "granted" | "denied" | "prompt" | "unsupported" | "error"
    };

    const requestNotifications = async () => {
        // On native Capacitor builds (Android/iOS), push runs through
        // @capacitor-firebase/messaging — the browser Notification API
        // used below has no bearing on that permission channel at all,
        // so calling it here never actually triggered the real OS
        // dialog on native builds; the genuine prompt only fired later,
        // uncoordinated, from initCapacitor()'s own automatic call on
        // boot (user-reported: notification permission not requested
        // correctly). ``requestPushPermission()`` is the same helper
        // NotificationPermissionCard.jsx (Profile page) already uses
        // for the real thing, and also immediately fetches + uploads
        // the FCM token on success so push works right away.
        const { Capacitor } = await import("@capacitor/core");
        if (Capacitor?.isNativePlatform?.()) {
            const r = await requestPushPermission();
            return r.state;   // "granted" | "denied" | "prompt" | "unknown" | "error"
        }
        // Web / installed-PWA fallback — no native plugin there.
        if (!("Notification" in window)) return "unsupported";
        try {
            const res = await Notification.requestPermission();
            return res;        // "granted" | "denied" | "default"
        } catch {
            return "error";
        }
    };

    // Hard ceiling per permission request — in Android Capacitor WebViews
    // getUserMedia / Notification.requestPermission occasionally never
    // resolve when the manifest permission is missing, leaving the user
    // staring at a frozen spinner (iter53). 7s is plenty for any genuine
    // OS prompt; anything longer is a hang.
    const withTimeout = (promise, ms = 7000, fallback = "timeout") =>
        Promise.race([
            promise,
            new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
        ]);

    const allow = async () => {
        setBusy(true);
        const results = {};
        // Sequential — we want each native prompt to finish before the
        // next one fires (stacking them confuses users). Each request
        // is timeout-capped so one hang can't trap the user.
        for (const it of items) {
            try {
                if (it.id === "location") {
                    results.location = await withTimeout(requestLocation());
                } else if (it.id === "notifications") {
                    results.notifications = await withTimeout(requestNotifications());
                }
            } catch {
                results[it.id] = "error";
            }
        }
        try { localStorage.setItem(DONE_KEY, JSON.stringify({ app, at: Date.now(), results })); } catch {/**/}
        const granted = Object.values(results).filter((v) => v === "granted").length;
        if (granted > 0) {
            toast.success(`${granted} of ${items.length} permission${granted === 1 ? "" : "s"} granted`);
        } else {
            toast.info("You can enable these later from device Settings");
        }
        setBusy(false);
        nav(next, { replace: true });
    };

    // iter98 — Apple Guideline 5.1.1(iv) rejection #2: removed the
    // "Not now" / skip path entirely. Apple now requires that ANY
    // pre-permission educational screen MUST always lead to the OS
    // permission prompt — no escape hatch on this screen. The user
    // retains full control at the OS-level dialog (Allow / Don't Allow)
    // and can re-grant later from device Settings if they decline.
    // DO NOT re-introduce a "Skip" / "Not now" / "Maybe later" button
    // without re-reading the rejection notice.

    return (
        // iter99 — iPad / tablet layout fix. The PermissionsPage is a
        // top-level route outside CustomerShell, so the .pwa-shell
        // responsive column doesn't auto-apply. Apple reviewers
        // screenshot the iPad view and the empty middle was very
        // pronounced. We deliberately use a TIGHTER max-w than
        // .pwa-shell (which expands to 760-1024px on iPad and looks
        // stretched for this 2-card screen). Capping at max-w-md
        // (~448px) keeps the page looking like a centered mobile-
        // shaped column on iPad while leaving phone behaviour
        // identical. The .pwa-bg backdrop + card border + shadow
        // appear only at md+ (≥768px) so phones still get edge-to-edge.
        <div className="pwa-bg min-h-screen safe-pl safe-pr md:flex md:items-stretch md:justify-center md:py-6">
            <div
                className="w-full max-w-md mx-auto bg-gradient-to-b from-background to-muted/40 flex flex-col min-h-screen md:min-h-0 md:rounded-3xl md:border md:border-border md:shadow-[0_0_60px_rgba(0,0,0,0.06)] md:overflow-hidden md:my-auto"
                data-testid={`permissions-page-${app}`}
            >
                <header className="px-5 pt-10 pb-4 text-center safe-pt">
                    <div className="inline-flex w-16 h-16 rounded-2xl bg-primary/10 items-center justify-center mb-3">
                        <ShieldCheck size={32} className="text-primary" />
                    </div>
                    <h1 className="text-2xl font-extrabold tracking-tight">Quick setup</h1>
                    <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto">
                        We&apos;ll ask for a few permissions so the app works smoothly.
                        You can change these later from device Settings.
                    </p>
                </header>
                <main className="flex-1 px-5">
                    <ul className="space-y-3" data-testid="permissions-list">
                        {items.map((it) => (
                            <li
                                key={it.id}
                                data-testid={`permission-card-${it.id}`}
                                className="bg-card border border-border rounded-2xl p-4 flex items-start gap-3"
                            >
                                <div className={`w-11 h-11 rounded-xl bg-muted ${it.color} inline-flex items-center justify-center shrink-0`}>
                                    <it.icon size={20} />
                                </div>
                                <div className="min-w-0">
                                    <p className="font-bold text-sm">{it.title}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{it.body}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </main>
                <footer className="sticky bottom-0 bg-background border-t border-border px-5 py-4 mt-6 safe-pb">
                    <div className="space-y-2.5">
                        <button
                            onClick={allow}
                            disabled={busy}
                            data-testid="permissions-allow"
                            className="w-full bg-primary text-primary-foreground rounded-2xl py-3.5 font-bold tracking-wide inline-flex items-center justify-center gap-2 active:scale-[0.98] transition disabled:opacity-60"
                        >
                            {busy && <Loader2 size={16} className="animate-spin" />}
                            Continue
                        </button>
                        {/*
                          * iter98 — App Store Guideline 5.1.1(iv) compliance.
                          * Apple's second rejection (Feb 2026) was explicit:
                          *   1. Button label MUST be "Continue" or "Next" —
                          *      NOT "Allow & continue" (the word "Allow"
                          *      implies the user is granting permission via
                          *      THIS tap, which is misleading because the OS
                          *      prompt fires AFTER this tap).
                          *   2. There MUST NOT be a "Skip" / "Not now" /
                          *      dismiss button on the pre-permission screen.
                          *      The user must always proceed to the OS
                          *      permission prompt — they can decline at the
                          *      OS-level dialog (their right), and re-grant
                          *      later from device Settings.
                          * DO NOT re-add a secondary button here without
                          * re-reading App Review's rejection notice.
                          */}
                    </div>
                </footer>
            </div>
        </div>
    );
}

/**
 * Helper: routed-to-permissions check from app shell.
 * Returns true once the user has gone through the screen (granted OR
 * skipped). Callers use this to decide whether to render the onboarding.
 */
export function hasCompletedPermissions() {
    try {
        return !!localStorage.getItem(DONE_KEY);
    } catch {
        return true;          // fail-open; never block in a busted env
    }
}
